<template>
  <div class="pagination">
    <cv-button
      class="icon-button btn-previous"
      kind="ghost"
      size="small"
      :disabled="disablePrevious"
      @click="handlePrevious"
    >
      <svg-icon name="ArrowLeft24" kind="action" />
    </cv-button>

    <div v-if="simple" class="simple-pages">
      <span class="current">{{ currentPageValue }}</span>
      <svg-icon name="ForwardSlash16" kind="indicative" />
      <span class="length">{{ totalPage }}</span>
    </div>

    <div v-else class="pages">
      <template v-if="showAllPages">
        <cv-button
          v-for="page in totalPage"
          :key="page"
          class="page"
          :class="{ current: page === currentPageValue }"
          kind="ghost"
          size="small"
          >{{ page }}
        </cv-button>
      </template>

      <template v-else>
        <cv-button
          v-for="page in limit"
          :key="page"
          class="page"
          :class="{ current: page === currentPageValue }"
          kind="ghost"
          size="small"
          >{{ page }}
        </cv-button>

        <template v-if="overflow">
          <div>...</div>
          <!-- <cv-select
          >
          <cv-select-option v-for="page in totalPage" :key="page">{{ page }}</cv-select-option>
        </cv-select> -->

          <cv-button
            class="page"
            :class="{ current: page === currentPageValue }"
            kind="ghost"
            size="small"
            >{{ totalPage }}
          </cv-button>
        </template>
      </template>
    </div>

    <cv-button
      class="icon-button btn-next"
      kind="ghost"
      size="small"
      :disabled="disableNext"
      @click="handleNext"
    >
      <svg-icon name="ArrowRight24" kind="action" />
    </cv-button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

// Props
interface Props {
  simple?: boolean;
  overflow?: boolean;
  previousButtonDisabled?: boolean;
  nextButtonDisabled?: boolean;
  currentPage?: number;
  totalPage?: number;
  limit?: number;
}

const {
  simple = false,
  overflow = false,
  previousButtonDisabled = false,
  nextButtonDisabled = false,
  currentPage = 1,
  totalPage = 1,
  limit = 5,
} = defineProps<Props>();

// Emits
const emit = defineEmits<{
  change: [data: unknown];
}>();

// Data
const currentPageValue = ref(currentPage);

// Computed
const showAllPages = computed(() => {
  return limit ? totalPage <= limit : true;
});

const disablePrevious = computed(() => {
  return previousButtonDisabled || currentPageValue.value === 1;
});

const disableNext = computed(() => {
  return nextButtonDisabled || currentPageValue.value === totalPage;
});

// Methods
const handlePrevious = () => {
  if (currentPageValue.value > 1) {
    currentPageValue.value--;
    emit("change", currentPageValue.value);
  }
};

const handleNext = () => {
  if (currentPageValue.value < totalPage) {
    currentPageValue.value++;
    emit("change", currentPageValue.value);
  }
};

// Watch
watch(
  () => currentPage,
  () => {
    currentPageValue.value = currentPage;
  },
);
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.pagination {
  @include carbon--type-style("button-01");
  display: flex;
  align-items: center;
  justify-content: center;

  .bx--btn.icon-button {
    .sls-icon {
      [stroke] {
        stroke: $ui-05;
      }
    }
  }

  .simple-pages {
    display: flex;
    align-items: center;

    span {
      width: 48px;
      margin: 0 $spacing-02;
      text-align: center;
    }

    .length {
      color: $disabled-02;
    }
  }

  .pages {
    display: flex;
    align-items: center;

    .page {
      width: 36px;
      min-width: 0;
      height: 36px;
      margin: 0 $spacing-03;
      color: $interactive-02;
      border-radius: 10px;

      &.current {
        background-color: $ui-05;
        color: $text-04;
      }

      &:focus {
        background-color: $ui-01;
        color: $interactive-02;
        border: 1px solid $focus;
      }

      &:hover {
        background-color: $hover-ui;
        color: $interactive-02;
      }
    }
  }
}
</style>
